import { useEffect, useMemo, useState, useCallback, Fragment } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Upload as UploadIcon, Download, CheckCircle2, AlertCircle, Trash2, Edit3, Search, BarChart3, X, FileMinus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatINR, formatDate } from "@/lib/format";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import Pagination, { usePagination } from "@/components/Pagination";

const FY_MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];

// Compact INR formatter for tight pivot cells
const compactINR = (n) => {
  if (!n) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${Math.round(n)}`;
};

// Derive FY (e.g. "2026-27") from a date string when invoice.fy is missing
const deriveFy = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const m = d.getMonth() + 1; // 1-12
  const y = d.getFullYear();
  if (m >= 4) return `${y}-${String(y + 1).slice(-2)}`;
  return `${y - 1}-${String(y).slice(-2)}`;
};

// 0-based month index in FY_MONTHS (Apr=0, ..., Mar=11)
const fyMonthIdx = (dateStr) => {
  if (!dateStr) return -1;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return -1;
  return (d.getMonth() + 1 - 4 + 12) % 12;
};

const EDIT_FIELDS = [
  ["invoice_number", "Invoice Number *", "text"],
  ["fy", "FY", "text"],
  ["party_client_name", "Party Client Name", "text"],
  ["project", "Project", "text"],
  ["subvendor_name", "Subvendor Name", "text"],
  ["rc_number", "RC Number", "text"],
  ["rc_amount", "RC Amount", "number"],
  ["rc_start_date", "RC Start Date", "date"],
  ["rc_end_date", "RC End Date", "date"],
  ["ro_no", "RO No", "text"],
  ["ro_date", "RO Date", "date"],
  ["invoice_date", "Invoice Date", "date"],
  ["invoice_service_period", "Service Period", "text"],
  ["base_amount", "Base Amount", "number"],
  ["gst_amount", "GST Amount", "number"],
  ["net_amount", "Net Amount", "number"],
];

export default function Upload() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const [invoices, setInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Upload-summary filters (FY × Month pivot on uploaded invoices)
  const [summaryFy, setSummaryFy] = useState("all");
  const [summaryProject, setSummaryProject] = useState("all");
  const [summaryClient, setSummaryClient] = useState("all");

  // Debounce search input by 350ms to avoid firing API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const loadInvoices = useCallback(async () => {
    const params = debouncedQ ? { q: debouncedQ } : {};
    const { data } = await api.get("/invoices", { params });
    setInvoices(data);
  }, [debouncedQ]);

  const loadAllInvoices = useCallback(async () => {
    const { data } = await api.get("/invoices");
    setAllInvoices(data);
  }, []);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);
  useEffect(() => { loadAllInvoices(); }, [loadAllInvoices]);

  // Credit notes (loaded eagerly so the FY × Month pivot Net row can use them)
  const [creditNotes, setCreditNotes] = useState([]);
  const loadCreditNotes = useCallback(async () => {
    try {
      const { data } = await api.get("/credit-notes");
      setCreditNotes(data || []);
    } catch (_e) {
      setCreditNotes([]);
    }
  }, []);
  useEffect(() => { loadCreditNotes(); }, [loadCreditNotes]);

  const onUpload = async () => {
    if (!file) return toast.error("Please select a file");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      // Phase 1: dry-run validation (no inserts)
      const { data } = await api.post("/excel/upload?dry_run=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Defensive: detect responses from older backends that don't return the new validation shape
      const isNewBackend = data && typeof data.ok === "boolean" && data.summary;
      if (!isNewBackend) {
        // Old backend behavior: it ignored ?dry_run, already inserted. Just show its response normalized.
        const inserted = Number(data?.inserted || 0);
        const skipped = Array.isArray(data?.skipped) ? data.skipped : [];
        setResult({
          inserted,
          errors: skipped.map((s) => ({ row: s.row, invoice_number: "", field: "", message: s.reason || "" })),
          _pending: false,
          _legacy: true,
        });
        if (inserted > 0) {
          toast.success(`${inserted} invoice(s) imported (legacy mode)`);
          loadInvoices();
          loadAllInvoices();
        } else {
          toast.error("Backend on the server appears to be running an older build. Please restart the backend service.");
        }
        return;
      }
      if (data.ok) {
        // Phase 2: no errors → commit immediately
        await commitUpload(false);
      } else {
        // Show validation report dialog for user decision
        setResult({ ...data, _pending: true });
        toast.error(`${data.summary?.error_count || 0} validation error(s) found. Review below.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const commitUpload = async (skipErrors) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/excel/upload?dry_run=false&skip_errors=${skipErrors ? "true" : "false"}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult({ ...data, _pending: false, errors: Array.isArray(data.errors) ? data.errors : [] });
      if ((data.inserted || 0) > 0) {
        toast.success(`${data.inserted} invoice(s) imported${data.errors?.length ? ` · ${data.errors.length} row(s) skipped` : ""}`);
        loadInvoices();
        loadAllInvoices();
      } else {
        toast.error("Nothing imported.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadErrorReport = () => {
    if (!result?.errors?.length) return;
    const aoa = [
      ["Row #", "Invoice Number", "Field", "Error Message"],
      ...result.errors.map((e) => [e.row, e.invoice_number || "", e.field || "", e.message || ""]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 8 }, { wch: 24 }, { wch: 18 }, { wch: 80 }];
    // Bold header row
    ["A1", "B1", "C1", "D1"].forEach((addr) => {
      if (ws[addr]) ws[addr].s = { font: { bold: true } };
    });
    XLSX.utils.book_append_sheet(wb, ws, "Errors");
    const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    XLSX.writeFile(wb, `invoice_upload_errors_${ts}.xlsx`);
    toast.success("Error report downloaded");
  };

  const cancelUpload = () => {
    setResult(null);
    setFile(null);
    const fi = document.querySelector('input[type="file"]');
    if (fi) fi.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/excel/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "invoice_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const startEdit = (inv) => {
    setEditing({
      id: inv.id,
      invoice_number: inv.invoice_number || "",
      fy: inv.fy || "",
      party_client_name: inv.party_client_name || "",
      project: inv.project || "",
      subvendor_name: inv.subvendor_name || "",
      rc_number: inv.rc_number || "",
      rc_amount: inv.rc_amount ?? 0,
      rc_start_date: inv.rc_start_date || "",
      rc_end_date: inv.rc_end_date || "",
      ro_no: inv.ro_no || "",
      ro_date: inv.ro_date || "",
      invoice_date: inv.invoice_date || "",
      invoice_description: inv.invoice_description || "",
      invoice_service_period: inv.invoice_service_period || "",
      base_amount: inv.base_amount ?? 0,
      gst_amount: inv.gst_amount ?? 0,
      net_amount: inv.net_amount ?? 0,
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editing) return;
    try {
      const payload = {
        ...editing,
        rc_amount: Number(editing.rc_amount || 0),
        base_amount: Number(editing.base_amount || 0),
        gst_amount: Number(editing.gst_amount || 0),
        net_amount: Number(editing.net_amount || (Number(editing.base_amount || 0) + Number(editing.gst_amount || 0))),
      };
      const { id, ...body } = payload;
      await api.put(`/invoices/${id}`, body);
      toast.success("Invoice updated");
      setEditOpen(false);
      setEditing(null);
      loadInvoices();
      loadAllInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      loadInvoices();
      loadAllInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // ---------- Upload Summary Pivot (FY × Month) ----------
  const summaryPivot = useMemo(() => {
    // Available filter options derived from both invoices and credit notes
    const fyOptSet = new Set();
    const projectOptSet = new Set();
    const clientOptSet = new Set();
    allInvoices.forEach((i) => {
      const f = i.fy || deriveFy(i.invoice_date);
      if (f) fyOptSet.add(f);
      if (i.project) projectOptSet.add(i.project);
      if (i.party_client_name) clientOptSet.add(i.party_client_name);
    });
    (creditNotes || []).forEach((c) => {
      const f = deriveFy(c.credit_note_date) || c.fy;
      if (f) fyOptSet.add(f);
      if (c.project) projectOptSet.add(c.project);
      if (c.party_client_name) clientOptSet.add(c.party_client_name);
    });
    const fyOptions = Array.from(fyOptSet).sort().reverse();
    const projectOptions = Array.from(projectOptSet).sort();
    const clientOptions = Array.from(clientOptSet).sort();

    const matches = allInvoices.filter((i) => {
      const fy = i.fy || deriveFy(i.invoice_date);
      if (summaryFy !== "all" && fy !== summaryFy) return false;
      if (summaryProject !== "all" && (i.project || "") !== summaryProject) return false;
      if (summaryClient !== "all" && (i.party_client_name || "") !== summaryClient) return false;
      return true;
    });
    const _invByNumFilter = new Map(allInvoices.map((i) => [i.invoice_number, i]));
    const _invByIdFilter = new Map(allInvoices.map((i) => [i.id, i]));
    const cnMatches = (creditNotes || []).filter((c) => {
      const parentInv = _invByIdFilter.get(c.invoice_id) || _invByNumFilter.get(c.invoice_number);
      const fy = (parentInv?.fy) || deriveFy(parentInv?.invoice_date) || deriveFy(c.credit_note_date) || c.fy || "";
      const project = parentInv?.project || c.project || "";
      const client = parentInv?.party_client_name || c.party_client_name || "";
      if (summaryFy !== "all" && fy !== summaryFy) return false;
      if (summaryProject !== "all" && project !== summaryProject) return false;
      if (summaryClient !== "all" && client !== summaryClient) return false;
      return true;
    });

    // Group: { fy → { month → {count, base, gst, net} } } plus per-FY totals
    const grid = {};
    const rowTotal = {}; // per-FY total
    const colTotal = Object.fromEntries(FY_MONTHS.map((m) => [m, { count: 0, base: 0, gst: 0, net: 0 }]));
    const grand = { count: 0, base: 0, gst: 0, net: 0 };
    let unknownDateCount = 0;

    matches.forEach((inv) => {
      const fy = inv.fy || deriveFy(inv.invoice_date) || "Unknown FY";
      const idx = fyMonthIdx(inv.invoice_date);
      if (idx < 0) {
        unknownDateCount += 1;
        return;
      }
      const month = FY_MONTHS[idx];
      const base = Number(inv.base_amount) || 0;
      const gst = Number(inv.gst_amount) || 0;
      const net = Number(inv.net_amount) || 0;
      grid[fy] = grid[fy] || Object.fromEntries(FY_MONTHS.map((m) => [m, { count: 0, base: 0, gst: 0, net: 0 }]));
      const cell = grid[fy][month];
      cell.count += 1; cell.base += base; cell.gst += gst; cell.net += net;
      rowTotal[fy] = rowTotal[fy] || { count: 0, base: 0, gst: 0, net: 0 };
      rowTotal[fy].count += 1; rowTotal[fy].base += base; rowTotal[fy].gst += gst; rowTotal[fy].net += net;
      colTotal[month].count += 1; colTotal[month].base += base; colTotal[month].gst += gst; colTotal[month].net += net;
      grand.count += 1; grand.base += base; grand.gst += gst; grand.net += net;
    });

    // CN grid · placed by the parent invoice's date (so "Invoice − CN = Net" matches per month)
    const invByNumber = new Map(allInvoices.map((i) => [i.invoice_number, i]));
    const invById = new Map(allInvoices.map((i) => [i.id, i]));
    const cnGrid = {};
    const cnRowTotal = {};
    const cnColTotal = Object.fromEntries(FY_MONTHS.map((m) => [m, { count: 0, total: 0 }]));
    const cnGrand = { count: 0, total: 0 };
    let cnUnknownDate = 0;
    cnMatches.forEach((cn) => {
      const parentInv = invById.get(cn.invoice_id) || invByNumber.get(cn.invoice_number);
      const placementDate = parentInv?.invoice_date || cn.credit_note_date;
      const fy = (parentInv?.fy) || deriveFy(placementDate) || cn.fy || "Unknown FY";
      const idx = fyMonthIdx(placementDate);
      if (idx < 0) { cnUnknownDate += 1; return; }
      const month = FY_MONTHS[idx];
      const amt = Number(cn.total_amount) || 0;
      cnGrid[fy] = cnGrid[fy] || Object.fromEntries(FY_MONTHS.map((m) => [m, { count: 0, total: 0 }]));
      cnGrid[fy][month].count += 1; cnGrid[fy][month].total += amt;
      cnRowTotal[fy] = cnRowTotal[fy] || { count: 0, total: 0 };
      cnRowTotal[fy].count += 1; cnRowTotal[fy].total += amt;
      cnColTotal[month].count += 1; cnColTotal[month].total += amt;
      cnGrand.count += 1; cnGrand.total += amt;
    });

    // Union of FY keys (so a CN-only FY still shows up)
    const fyKeys = Array.from(new Set([...Object.keys(grid), ...Object.keys(cnGrid)])).sort().reverse();
    const hasFilter = summaryFy !== "all" || summaryProject !== "all" || summaryClient !== "all";
    const hasAnyCN = cnGrand.count > 0;

    return {
      fyOptions, projectOptions, clientOptions,
      grid, rowTotal, colTotal, grand,
      cnGrid, cnRowTotal, cnColTotal, cnGrand,
      fyKeys, hasFilter, hasAnyCN,
      unknownDateCount, cnUnknownDate, matchCount: matches.length,
    };
  }, [allInvoices, creditNotes, summaryFy, summaryProject, summaryClient]);

  const {
    pageItems: pagedInvoices,
    page: uplPage, setPage: setUplPage,
    pageSize: uplPageSize, setPageSize: setUplPageSize,
    totalPages: uplTotalPages, totalItems: uplTotalItems,
  } = usePagination(invoices, "voltledger.upload.pageSize");

  // ----- Credit Notes tab state -----
  const [tab, setTab] = useState("upload"); // "upload" | "credit_notes"
  const todayIsoStr = new Date().toISOString().slice(0, 10);
  const [cnForm, setCnForm] = useState({
    invoice_number: "",
    credit_note_number: "",
    credit_note_date: todayIsoStr,
    base_amount: "",
    gst_amount: "",
    is_full_cancellation: false,
    reason: "",
  });
  const [cnBusy, setCnBusy] = useState(false);
  const [cnSearch, setCnSearch] = useState("");
  // Bulk CN upload
  const [cnFile, setCnFile] = useState(null);
  const [cnDryRun, setCnDryRun] = useState(true);
  const [cnUploadResult, setCnUploadResult] = useState(null);
  const [cnUploadBusy, setCnUploadBusy] = useState(false);

  const submitCN = async () => {
    if (!cnForm.invoice_number || !cnForm.credit_note_number || !cnForm.credit_note_date) {
      return toast.error("Invoice #, Credit Note # and Date are required");
    }
    try {
      setCnBusy(true);
      const payload = {
        invoice_number: cnForm.invoice_number.trim(),
        credit_note_number: cnForm.credit_note_number.trim(),
        credit_note_date: cnForm.credit_note_date,
        base_amount: Number(cnForm.base_amount || 0),
        gst_amount: Number(cnForm.gst_amount || 0),
        is_full_cancellation: !!cnForm.is_full_cancellation,
        reason: cnForm.reason || "",
      };
      await api.post("/credit-notes", payload);
      toast.success("Credit Note saved");
      setCnForm({ invoice_number: "", credit_note_number: "", credit_note_date: todayIsoStr, base_amount: "", gst_amount: "", is_full_cancellation: false, reason: "" });
      loadCreditNotes();
      loadAllInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setCnBusy(false);
    }
  };

  const deleteCN = async (cn) => {
    if (!window.confirm(`Delete CN ${cn.credit_note_number}? Outstanding on ${cn.invoice_number} will recalc.`)) return;
    try {
      await api.delete(`/credit-notes/${cn.id}`);
      toast.success("Credit Note deleted");
      loadCreditNotes();
      loadAllInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const downloadCnTemplate = async () => {
    try {
      const res = await api.get("/credit-notes/template", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `credit_notes_template_${todayIsoStr.replace(/-/g, "")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Template download failed");
    }
  };

  const runCnBulk = async () => {
    if (!cnFile) return toast.error("Choose an Excel file first");
    try {
      setCnUploadBusy(true);
      const fd = new FormData();
      fd.append("file", cnFile);
      const { data } = await api.post(`/credit-notes/upload?dry_run=${cnDryRun}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCnUploadResult(data);
      if (!cnDryRun && data.queued_count > 0) {
        toast.success(`${data.queued_count} credit note(s) saved`);
        loadCreditNotes();
        loadAllInvoices();
      } else if (cnDryRun) {
        toast.success(`Preview · ${data.queued_count} ready · ${data.error_count} error(s)`);
      } else {
        toast.error(`Nothing saved · ${data.error_count} error(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setCnUploadBusy(false);
    }
  };

  const cnApplyAfterPreview = async () => {
    if (!window.confirm(`Apply ${cnUploadResult?.queued_count || 0} credit note(s)?`)) return;
    setCnDryRun(false);
    setTimeout(async () => {
      try {
        setCnUploadBusy(true);
        const fd = new FormData();
        fd.append("file", cnFile);
        const { data } = await api.post(`/credit-notes/upload?dry_run=false`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setCnUploadResult(data);
        if (data.queued_count > 0) {
          toast.success(`${data.queued_count} credit note(s) saved`);
          loadCreditNotes();
          loadAllInvoices();
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Apply failed");
      } finally {
        setCnUploadBusy(false);
      }
    }, 60);
  };

  const filteredCNs = useMemo(() => {
    const needle = cnSearch.trim().toLowerCase();
    if (!needle) return creditNotes;
    return creditNotes.filter((c) =>
      `${c.credit_note_number} ${c.invoice_number} ${c.party_client_name || ""} ${c.reason || ""}`.toLowerCase().includes(needle)
    );
  }, [creditNotes, cnSearch]);

  const cnInvoiceComparison = useMemo(() => {
    // Map: invoice_number → {net, cn_total, full, count}
    const map = {};
    creditNotes.forEach((c) => {
      const m = map[c.invoice_number] || { invoice_number: c.invoice_number, party: c.party_client_name || "", project: c.project || "", cn_total: 0, full: false, count: 0 };
      m.cn_total = +(m.cn_total + (c.total_amount || 0)).toFixed(2);
      m.full = m.full || !!c.is_full_cancellation;
      m.count += 1;
      map[c.invoice_number] = m;
    });
    // Pair with invoice net
    Object.values(map).forEach((m) => {
      const inv = allInvoices.find((i) => i.invoice_number === m.invoice_number);
      m.net = inv?.net_amount || 0;
      m.outstanding = inv?.outstanding ?? 0;
      m.is_voided = !!inv?.is_voided;
    });
    return Object.values(map).sort((a, b) => a.invoice_number.localeCompare(b.invoice_number));
  }, [creditNotes, allInvoices]);

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1600px]">
      <div className="mb-8">
        <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">Bulk Import</div>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Excel Upload</h1>
        <p className="text-sm text-slate-500 mt-2">Upload invoices in bulk using the standard format. Duplicate invoice numbers are skipped.</p>
      </div>

      {/* Tab strip */}
      <div className="mb-6 flex items-center gap-1 border-b border-slate-200" data-testid="upload-tabs">
        <button
          onClick={() => setTab("upload")}
          className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors -mb-px ${tab === "upload" ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-upload"
        >
          <UploadIcon className="w-3.5 h-3.5 inline mr-1.5" /> Invoice Upload
        </button>
        <button
          onClick={() => setTab("credit_notes")}
          className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors -mb-px ${tab === "credit_notes" ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-credit-notes"
        >
          <FileMinus className="w-3.5 h-3.5 inline mr-1.5" /> Credit Notes
        </button>
      </div>

      {tab === "credit_notes" ? (
        <div data-testid="credit-notes-tab">
          {/* ===== Manual Single CN ===== */}
          <div className="bg-white border border-slate-200 p-6 mb-6">
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">Raise Credit Note</h3>
            <p className="text-xs text-slate-500 mb-4">Reduces invoice outstanding by the CN amount. Toggle <strong>Full Cancellation</strong> to null & void the entire outstanding (status becomes <em>Credit Note Issued</em>).</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Invoice # *</Label>
                <Input value={cnForm.invoice_number} onChange={(e) => setCnForm({ ...cnForm, invoice_number: e.target.value })} placeholder="e.g. RCPL/0035/26-27" className="rounded-none mt-1" data-testid="cn-invoice-number" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Credit Note # *</Label>
                <Input value={cnForm.credit_note_number} onChange={(e) => setCnForm({ ...cnForm, credit_note_number: e.target.value })} placeholder="e.g. CN-0001-26-27" className="rounded-none mt-1" data-testid="cn-number" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Credit Note Date *</Label>
                <Input type="date" value={cnForm.credit_note_date} onChange={(e) => setCnForm({ ...cnForm, credit_note_date: e.target.value })} className="rounded-none mt-1" data-testid="cn-date" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Base Amount</Label>
                <Input type="number" value={cnForm.base_amount} onChange={(e) => setCnForm({ ...cnForm, base_amount: e.target.value })} className="rounded-none mt-1" placeholder="0.00" data-testid="cn-base" />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">GST Amount</Label>
                <Input type="number" value={cnForm.gst_amount} onChange={(e) => setCnForm({ ...cnForm, gst_amount: e.target.value })} className="rounded-none mt-1" placeholder="0.00" data-testid="cn-gst" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="cn-full"
                  checked={cnForm.is_full_cancellation}
                  onChange={(e) => setCnForm({ ...cnForm, is_full_cancellation: e.target.checked })}
                  data-testid="cn-full-cancel"
                  className="accent-rose-600 w-4 h-4"
                />
                <label htmlFor="cn-full" className="text-xs font-semibold text-rose-700 cursor-pointer">Full Cancellation · null & void entire outstanding</label>
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Reason</Label>
              <Input value={cnForm.reason} onChange={(e) => setCnForm({ ...cnForm, reason: e.target.value })} placeholder="Short description for audit" className="rounded-none mt-1" data-testid="cn-reason" />
            </div>
            <div className="flex items-center gap-3 mt-5 pt-4 border-t border-slate-100">
              <Button onClick={submitCN} disabled={cnBusy} className="bg-amber-600 hover:bg-amber-700 rounded-none" data-testid="cn-submit">
                {cnBusy ? "Saving…" : "Save Credit Note"}
              </Button>
              <div className="text-[10px] text-slate-400">Total: ₹{(Number(cnForm.base_amount || 0) + Number(cnForm.gst_amount || 0)).toLocaleString("en-IN")}</div>
            </div>
          </div>

          {/* ===== Bulk CN Upload ===== */}
          <div className="bg-white border border-slate-200 p-6 mb-6">
            <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
              <div>
                <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">Bulk Credit Notes via Excel</h3>
                <p className="text-xs text-slate-500">Step 1: Download template → Step 2: Fill rows → Step 3: Preview & apply.</p>
              </div>
              <Button onClick={downloadCnTemplate} variant="outline" className="rounded-none h-10 border-slate-300" data-testid="cn-template-btn">
                <Download className="w-4 h-4 mr-1.5" /> Template
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Excel (.xlsx)</Label>
                <Input type="file" accept=".xlsx,.xls" onChange={(e) => { setCnFile(e.target.files?.[0] || null); setCnUploadResult(null); }} className="rounded-none mt-1" data-testid="cn-bulk-file" />
              </div>
              <label className="flex items-center gap-2 text-xs pt-6 cursor-pointer">
                <input type="checkbox" checked={cnDryRun} onChange={(e) => setCnDryRun(e.target.checked)} className="accent-[#002FA7]" data-testid="cn-bulk-dry-run" />
                <span>Dry-run preview first</span>
              </label>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Button onClick={runCnBulk} disabled={cnUploadBusy || !cnFile} className={`rounded-none ${cnDryRun ? "bg-indigo-600 hover:bg-indigo-700" : "bg-amber-600 hover:bg-amber-700"}`} data-testid="cn-bulk-submit">
                {cnUploadBusy ? "Processing…" : (cnDryRun ? "Preview" : "Upload")}
              </Button>
              {cnUploadResult && cnUploadResult.dry_run && cnUploadResult.queued_count > 0 && (
                <Button onClick={cnApplyAfterPreview} disabled={cnUploadBusy} className="rounded-none bg-emerald-600 hover:bg-emerald-700" data-testid="cn-bulk-apply">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Apply {cnUploadResult.queued_count} Now
                </Button>
              )}
            </div>
            {cnUploadResult && (
              <div className="border border-slate-200 mt-4 max-h-72 overflow-auto" data-testid="cn-bulk-result">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs flex flex-wrap items-center gap-3">
                  <span className="text-emerald-700 font-semibold">{cnUploadResult.queued_count} {cnUploadResult.dry_run ? "ready" : "saved"}</span>
                  <span className="text-red-700 font-semibold ml-auto">{cnUploadResult.error_count} error(s)</span>
                </div>
                {cnUploadResult.errors.length > 0 && (
                  <table className="w-full text-xs">
                    <thead className="bg-red-50/60"><tr><th className="text-left px-3 py-1.5 text-[10px] uppercase text-red-700">Row</th><th className="text-left px-3 py-1.5 text-[10px] uppercase text-red-700">Invoice</th><th className="text-left px-3 py-1.5 text-[10px] uppercase text-red-700">Reason</th></tr></thead>
                    <tbody>
                      {cnUploadResult.errors.map((e, i) => (
                        <tr key={i} className="border-b border-red-100"><td className="px-3 py-1.5 font-mono-data">{e.row}</td><td className="px-3 py-1.5 font-mono-data">{e.invoice_number || "—"}</td><td className="px-3 py-1.5 text-red-700">{e.reason}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          {/* ===== Credit Notes List ===== */}
          <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">All Credit Notes <span className="text-xs text-slate-500 font-normal">· {creditNotes.length} total</span></h3>
            </div>
            <div className="relative w-72">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <Input value={cnSearch} onChange={(e) => setCnSearch(e.target.value)} placeholder="Search CN/invoice/client/reason…" className="rounded-none pl-9 h-10" data-testid="cn-search" />
            </div>
          </div>

          <div className="bg-white border border-slate-200 overflow-x-auto mb-8" data-testid="cn-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">CN #</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Date</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Invoice #</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Client / Project</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Base</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">GST</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Type</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Reason</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCNs.length === 0 && (
                  <tr><td colSpan={10} className="text-center text-sm text-slate-400 py-12">{creditNotes.length === 0 ? "No credit notes yet." : "No matches."}</td></tr>
                )}
                {filteredCNs.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100" data-testid={`cn-row-${c.credit_note_number}`}>
                    <td className="px-3 py-2 font-mono-data font-semibold text-slate-900">{c.credit_note_number}</td>
                    <td className="px-3 py-2 text-slate-700">{formatDate(c.credit_note_date)}</td>
                    <td className="px-3 py-2 font-mono-data text-indigo-700"><Link to={`/invoices/${c.invoice_id}`} className="hover:underline">{c.invoice_number}</Link></td>
                    <td className="px-3 py-2 text-xs text-slate-700">{c.party_client_name}<div className="text-[10px] text-slate-400">{c.project}</div></td>
                    <td className="px-3 py-2 text-right font-mono-data">{formatINR(c.base_amount)}</td>
                    <td className="px-3 py-2 text-right font-mono-data">{formatINR(c.gst_amount)}</td>
                    <td className="px-3 py-2 text-right font-mono-data font-bold text-slate-900">{formatINR(c.total_amount)}</td>
                    <td className="px-3 py-2">
                      {c.is_full_cancellation ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200">FULL · VOID</span>
                      ) : (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">Partial</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-600" title={c.reason}>{c.reason || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => deleteCN(c)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700" data-testid={`cn-del-${c.credit_note_number}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ===== Comparison ===== */}
          {cnInvoiceComparison.length > 0 && (
            <>
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">Invoice vs Credit Notes Comparison</h3>
              <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="cn-comparison">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500">Invoice #</th>
                      <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500">Client</th>
                      <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500">Invoice Net</th>
                      <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500">CN Total</th>
                      <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500">CN Count</th>
                      <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500">Outstanding</th>
                      <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider text-slate-500">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cnInvoiceComparison.map((m) => {
                      const ratio = m.net ? (m.cn_total / m.net) * 100 : 0;
                      return (
                        <tr key={m.invoice_number} className="border-b border-slate-100" data-testid={`cn-compare-${m.invoice_number}`}>
                          <td className="px-3 py-2 font-mono-data text-indigo-700">{m.invoice_number}</td>
                          <td className="px-3 py-2 text-slate-700">{m.party}<div className="text-[10px] text-slate-400">{m.project}</div></td>
                          <td className="px-3 py-2 text-right font-mono-data text-slate-700">{formatINR(m.net)}</td>
                          <td className="px-3 py-2 text-right font-mono-data text-amber-700 font-semibold">{formatINR(m.cn_total)}</td>
                          <td className="px-3 py-2 text-right font-mono-data">{m.count}</td>
                          <td className={`px-3 py-2 text-right font-mono-data font-semibold ${m.outstanding > 0 ? "text-slate-900" : "text-emerald-600"}`}>{formatINR(m.outstanding)}</td>
                          <td className="px-3 py-2">
                            {m.is_voided ? (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200">Voided</span>
                            ) : m.cn_total > 0 ? (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">{ratio.toFixed(1)}% adjusted</span>
                            ) : (
                              <span className="text-[10px] text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 bg-white border border-slate-200 p-8">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Upload Spreadsheet</h3>
          <label className="block border-2 border-dashed border-slate-300 p-12 text-center cursor-pointer hover:border-[#002FA7] transition-colors" data-testid="upload-dropzone">
            <UploadIcon className="w-8 h-8 mx-auto text-slate-400 mb-3" />
            <div className="text-sm font-medium text-slate-700">
              {file ? file.name : "Click to select .xlsx file"}
            </div>
            <div className="text-[11px] text-slate-400 mt-1">Only .xlsx files accepted</div>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="upload-file-input"
            />
          </label>
          <div className="mt-5 flex gap-3">
            <Button
              onClick={onUpload}
              disabled={!file || busy}
              data-testid="upload-submit-btn"
              className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-none h-10"
            >
              {busy ? "Uploading…" : "Upload & Import"}
            </Button>
            <Button onClick={downloadTemplate} variant="outline" className="rounded-none h-10" data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-1.5" /> Download Template
            </Button>
          </div>

          {result && (
            <div className="mt-6 border-t border-slate-100 pt-5">
              {result._pending ? (
                /* Pending decision — errors found, awaiting user action */
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-700">
                    <AlertCircle className="w-4 h-4" /> Validation found {result.summary?.error_count ?? 0} error(s) across {result.summary?.rows_parsed ?? 0} parsed row(s) ({result.summary?.valid_count ?? 0} valid)
                  </div>
                  <div className="border border-amber-200 bg-amber-50/70 px-3 py-2 rounded text-[12px] text-amber-900 leading-snug">
                    The rows below have errors and <strong>will NOT be imported</strong>. You can either fix them in Excel and re-upload, or proceed to import only the <strong>{result.summary.valid_count} valid row(s)</strong>, skipping the errors.
                  </div>
                  <div className="border border-slate-200 bg-white max-h-56 overflow-auto">
                    <table className="w-full text-[12px]">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold text-slate-700">Row #</th>
                          <th className="text-left px-3 py-2 font-semibold text-slate-700">Invoice #</th>
                          <th className="text-left px-3 py-2 font-semibold text-slate-700">Field</th>
                          <th className="text-left px-3 py-2 font-semibold text-slate-700">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.errors.map((e, i) => (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="px-3 py-1.5 font-mono-data text-slate-900">{e.row}</td>
                            <td className="px-3 py-1.5 text-slate-700">{e.invoice_number || "—"}</td>
                            <td className="px-3 py-1.5 text-slate-700">{e.field || "—"}</td>
                            <td className="px-3 py-1.5 text-rose-700">{e.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button onClick={downloadErrorReport} variant="outline" className="rounded-none h-9 border-slate-300" data-testid="download-error-report-btn">
                      <Download className="w-4 h-4 mr-1.5" /> Download Error Report
                    </Button>
                    <Button onClick={() => commitUpload(true)} disabled={busy || (result.summary?.valid_count || 0) === 0} className="bg-emerald-600 hover:bg-emerald-700 rounded-none h-9" data-testid="upload-valid-rows-btn">
                      <CheckCircle2 className="w-4 h-4 mr-1.5" /> Upload {result.summary?.valid_count ?? 0} Valid Row(s) Only
                    </Button>
                    <Button onClick={cancelUpload} variant="outline" className="rounded-none h-9 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="cancel-upload-btn">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                /* Final result */
                <>
                  <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700 mb-3">
                    <CheckCircle2 className="w-4 h-4" /> {result.inserted} invoices imported
                  </div>
                  {result.errors?.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-sm font-semibold text-amber-700">
                          <AlertCircle className="w-4 h-4" /> {result.errors.length} rows skipped
                        </div>
                        <Button onClick={downloadErrorReport} size="sm" variant="outline" className="rounded-none h-8 border-slate-300 text-xs" data-testid="download-error-report-btn-final">
                          <Download className="w-3.5 h-3.5 mr-1.5" /> Download Errors
                        </Button>
                      </div>
                      <ul className="text-xs text-slate-600 space-y-0.5 max-h-40 overflow-y-auto">
                        {result.errors.map((s, i) => (
                          <li key={i}>Row {s.row}{s.invoice_number ? ` · ${s.invoice_number}` : ""}: {s.message}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="bg-slate-900 text-white p-8">
          <h3 className="font-display text-lg font-semibold mb-4">Expected Format</h3>
          <p className="text-xs text-white/70 mb-4">Sheet name: <span className="font-mono-data text-white">Invoice Master Sheet</span></p>
          <ol className="space-y-1.5 text-[12px] text-white/90 list-decimal list-inside">
            <li>SN</li>
            <li>FY</li>
            <li>Party Client Name</li>
            <li>Project</li>
            <li>Subvendor Name</li>
            <li>RC Number</li>
            <li>RC Amount</li>
            <li>RC Start Date</li>
            <li>RC End Date</li>
            <li>RO No</li>
            <li>RO Date</li>
            <li>Invoice Number *</li>
            <li>Invoice Date</li>
            <li>Invoice Description</li>
            <li>Invoice Service Period</li>
            <li>Invoice Base Amount</li>
            <li>GST Amount</li>
            <li>Invoice Net Amount</li>
          </ol>
          <p className="text-[10px] text-white/60 mt-4">Invoice Number is mandatory and must be unique.</p>
        </div>
      </div>

      {/* Upload Summary · FY × Month pivot */}
      <div className="mb-10" data-testid="upload-summary-section">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400 flex items-center gap-1.5">
              <BarChart3 className="w-3 h-3" /> Upload Summary
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-1">Total Invoice Amount · FY × Month</h2>
            <p className="text-xs text-slate-500 mt-1">
              {summaryPivot.matchCount.toLocaleString("en-IN")} invoice{summaryPivot.matchCount === 1 ? "" : "s"}{summaryPivot.hasFilter ? " (filtered)" : " uploaded"} ·
              Grand total <strong className="text-slate-900 font-mono-data">{formatINR(summaryPivot.grand.net)}</strong>
              {summaryPivot.unknownDateCount > 0 && (
                <span className="ml-2 text-amber-600">· {summaryPivot.unknownDateCount} row(s) skipped (no invoice date)</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="upload-summary-filters">
            <Select value={summaryFy} onValueChange={setSummaryFy}>
              <SelectTrigger className="h-9 w-36 rounded-none border-slate-300 text-xs" data-testid="upload-summary-fy">
                <SelectValue placeholder="All FYs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All FYs</SelectItem>
                {summaryPivot.fyOptions.map((fy) => <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={summaryProject} onValueChange={setSummaryProject}>
              <SelectTrigger className="h-9 w-44 rounded-none border-slate-300 text-xs" data-testid="upload-summary-project">
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {summaryPivot.projectOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={summaryClient} onValueChange={setSummaryClient}>
              <SelectTrigger className="h-9 w-44 rounded-none border-slate-300 text-xs" data-testid="upload-summary-client">
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {summaryPivot.clientOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {summaryPivot.hasFilter && (
              <button
                onClick={() => { setSummaryFy("all"); setSummaryProject("all"); setSummaryClient("all"); }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-red-600 border border-slate-200 hover:border-red-300 rounded-none"
                data-testid="upload-summary-reset"
              >
                <X className="w-3 h-3" /> Reset
              </button>
            )}
          </div>
        </div>

        <div className="card-surface rounded-sm bg-white">
          <div className="overflow-auto" style={{ maxHeight: "560px" }}>
            <table className="w-full text-sm border-collapse">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr className="border-b-2 border-slate-400">
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300 sticky left-0 z-20">FY</th>
                  {summaryPivot.hasAnyCN && (
                    <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">Type</th>
                  )}
                  {FY_MONTHS.map((m) => (
                    <th key={m} className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-300">{m}</th>
                  ))}
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-900 bg-indigo-100 border border-indigo-300">FY Total</th>
                </tr>
              </thead>
              <tbody>
                {summaryPivot.fyKeys.length === 0 && (
                  <tr><td colSpan={summaryPivot.hasAnyCN ? 15 : 14} className="text-center text-sm text-slate-400 py-10">
                    {allInvoices.length === 0 ? "No invoices uploaded yet." : "No invoices match the selected filters."}
                  </td></tr>
                )}
                {summaryPivot.fyKeys.map((fy) => {
                  const invRow = summaryPivot.rowTotal[fy] || { count: 0, base: 0, gst: 0, net: 0 };
                  const cnRow = summaryPivot.cnRowTotal[fy] || { count: 0, total: 0 };
                  const monthsInv = summaryPivot.grid[fy] || Object.fromEntries(FY_MONTHS.map((m) => [m, { count: 0, base: 0, gst: 0, net: 0 }]));
                  const monthsCn = summaryPivot.cnGrid[fy] || Object.fromEntries(FY_MONTHS.map((m) => [m, { count: 0, total: 0 }]));

                  // Simple (no CN anywhere) → keep single-row layout
                  if (!summaryPivot.hasAnyCN) {
                    return (
                      <tr key={fy} className="border-b border-slate-200 hover:bg-slate-50/60" data-testid={`upload-summary-row-${fy}`}>
                        <td className="px-3 py-2.5 font-semibold text-slate-900 border border-slate-200 sticky left-0 bg-white z-10">FY {fy}</td>
                        {FY_MONTHS.map((m) => {
                          const c = monthsInv[m];
                          if (!c.count) return <td key={m} className="px-3 py-2 text-right text-slate-300 border border-slate-200">—</td>;
                          return (
                            <td key={m} className="px-3 py-2 text-right border border-slate-200" title={`${c.count} invoice(s) · Base ${formatINR(c.base)} · GST ${formatINR(c.gst)} · Net ${formatINR(c.net)}`}>
                              <div className="font-mono-data text-slate-900 font-semibold leading-tight">{compactINR(c.net)}</div>
                              <div className="text-[9px] text-slate-400 uppercase tracking-wider leading-tight">{c.count} inv</div>
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-right border border-indigo-300 bg-indigo-50">
                          <div className="font-mono-data text-indigo-900 font-bold leading-tight">{formatINR(invRow.net)}</div>
                          <div className="text-[9px] text-indigo-600 uppercase tracking-wider leading-tight">{invRow.count} inv</div>
                        </td>
                      </tr>
                    );
                  }

                  // CN exists somewhere → 3-row layout per FY
                  return (
                    <Fragment key={fy}>
                      <tr className="border-t-2 border-slate-300 hover:bg-slate-50/60" data-testid={`upload-summary-row-${fy}-invoice`}>
                        <td rowSpan={3} className="px-3 py-2.5 font-semibold text-slate-900 border border-slate-200 sticky left-0 bg-white z-10 align-top">
                          <div>FY {fy}</div>
                          <div className="text-[9px] text-slate-400 uppercase tracking-wider mt-1">{invRow.count} inv{cnRow.count > 0 ? ` · ${cnRow.count} CN` : ""}</div>
                        </td>
                        <td className="px-3 py-2 border border-slate-200 text-[10px] font-semibold uppercase tracking-wider text-slate-600 bg-slate-50/50">Invoice</td>
                        {FY_MONTHS.map((m) => {
                          const c = monthsInv[m];
                          if (!c.count) return <td key={m} className="px-3 py-2 text-right text-slate-300 border border-slate-200">—</td>;
                          return (
                            <td key={m} className="px-3 py-2 text-right border border-slate-200" title={`${c.count} invoice(s) · Base ${formatINR(c.base)} · GST ${formatINR(c.gst)} · Net ${formatINR(c.net)}`}>
                              <div className="font-mono-data text-slate-900 font-semibold leading-tight">{compactINR(c.net)}</div>
                              <div className="text-[9px] text-slate-400 uppercase tracking-wider leading-tight">{c.count} inv</div>
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-right border border-indigo-300 bg-indigo-50">
                          <div className="font-mono-data text-indigo-900 font-bold leading-tight">{formatINR(invRow.net)}</div>
                          <div className="text-[9px] text-indigo-600 uppercase tracking-wider leading-tight">{invRow.count} inv</div>
                        </td>
                      </tr>
                      <tr className="hover:bg-amber-50/40" data-testid={`upload-summary-row-${fy}-cn`}>
                        <td className="px-3 py-2 border border-slate-200 text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50/50">− Credit Notes</td>
                        {FY_MONTHS.map((m) => {
                          const c = monthsCn[m];
                          if (!c.count) return <td key={m} className="px-3 py-2 text-right text-slate-300 border border-slate-200">—</td>;
                          return (
                            <td key={m} className="px-3 py-2 text-right border border-amber-200 bg-amber-50/30" title={`${c.count} credit note(s) · Total ${formatINR(c.total)}`}>
                              <div className="font-mono-data text-amber-700 font-semibold leading-tight">−{compactINR(c.total)}</div>
                              <div className="text-[9px] text-amber-500 uppercase tracking-wider leading-tight">{c.count} cn</div>
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-right border border-amber-300 bg-amber-50/60">
                          {cnRow.count > 0 ? (
                            <>
                              <div className="font-mono-data text-amber-700 font-bold leading-tight">−{formatINR(cnRow.total)}</div>
                              <div className="text-[9px] text-amber-500 uppercase tracking-wider leading-tight">{cnRow.count} cn</div>
                            </>
                          ) : (
                            <div className="text-slate-300">—</div>
                          )}
                        </td>
                      </tr>
                      <tr className="border-b-2 border-slate-300 bg-emerald-50/40" data-testid={`upload-summary-row-${fy}-net`}>
                        <td className="px-3 py-2 border border-emerald-200 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50">= Net</td>
                        {FY_MONTHS.map((m) => {
                          const c = monthsInv[m];
                          const cn = monthsCn[m];
                          if (!c.count && !cn.count) return <td key={m} className="px-3 py-2 text-right text-slate-300 border border-emerald-200">—</td>;
                          const netVal = (c.net || 0) - (cn.total || 0);
                          return (
                            <td key={m} className="px-3 py-2 text-right border border-emerald-200" title={`Invoice ${formatINR(c.net)} − CN ${formatINR(cn.total)} = ${formatINR(netVal)}`}>
                              <div className="font-mono-data text-emerald-800 font-bold leading-tight">{compactINR(netVal)}</div>
                              <div className="text-[9px] text-emerald-600 uppercase tracking-wider leading-tight">net</div>
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-right border border-emerald-400 bg-emerald-100">
                          <div className="font-mono-data text-emerald-900 font-bold leading-tight">{formatINR((invRow.net || 0) - (cnRow.total || 0))}</div>
                          <div className="text-[9px] text-emerald-700 uppercase tracking-wider leading-tight">net</div>
                        </td>
                      </tr>
                    </Fragment>
                  );
                })}
                {summaryPivot.fyKeys.length > 0 && !summaryPivot.hasAnyCN && (
                  <tr className="border-t-2 border-indigo-400 bg-indigo-50" data-testid="upload-summary-grand-total">
                    <td className="px-3 py-2.5 font-bold uppercase tracking-[0.18em] text-[10px] text-indigo-900 border border-indigo-300 sticky left-0 bg-indigo-50 z-10">Total · All FYs</td>
                    {FY_MONTHS.map((m) => {
                      const c = summaryPivot.colTotal[m];
                      if (!c.count) return <td key={m} className="px-3 py-2 text-right text-indigo-200 border border-indigo-300 bg-indigo-50">—</td>;
                      return (
                        <td key={m} className="px-3 py-2 text-right border border-indigo-300 bg-indigo-50">
                          <div className="font-mono-data text-indigo-900 font-bold leading-tight">{compactINR(c.net)}</div>
                          <div className="text-[9px] text-indigo-600 uppercase tracking-wider leading-tight">{c.count} inv</div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right border border-indigo-400 bg-indigo-100">
                      <div className="font-mono-data text-indigo-900 font-bold leading-tight">{formatINR(summaryPivot.grand.net)}</div>
                      <div className="text-[9px] text-indigo-700 uppercase tracking-wider leading-tight">{summaryPivot.grand.count} inv</div>
                    </td>
                  </tr>
                )}
                {summaryPivot.fyKeys.length > 0 && summaryPivot.hasAnyCN && (
                  <>
                    <tr className="border-t-2 border-indigo-400 bg-indigo-50" data-testid="upload-summary-grand-total-invoice">
                      <td rowSpan={3} className="px-3 py-2.5 font-bold uppercase tracking-[0.18em] text-[10px] text-indigo-900 border border-indigo-300 sticky left-0 bg-indigo-50 z-10 align-top">
                        <div>Total · All FYs</div>
                        <div className="text-[9px] font-semibold text-indigo-600 mt-1 normal-case tracking-normal">{summaryPivot.grand.count} inv · {summaryPivot.cnGrand.count} CN</div>
                      </td>
                      <td className="px-3 py-2 border border-indigo-300 text-[10px] font-bold uppercase tracking-wider text-indigo-900 bg-indigo-50">Invoice</td>
                      {FY_MONTHS.map((m) => {
                        const c = summaryPivot.colTotal[m];
                        if (!c.count) return <td key={m} className="px-3 py-2 text-right text-indigo-200 border border-indigo-300 bg-indigo-50">—</td>;
                        return (
                          <td key={m} className="px-3 py-2 text-right border border-indigo-300 bg-indigo-50">
                            <div className="font-mono-data text-indigo-900 font-bold leading-tight">{compactINR(c.net)}</div>
                            <div className="text-[9px] text-indigo-600 uppercase tracking-wider leading-tight">{c.count} inv</div>
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right border border-indigo-400 bg-indigo-100">
                        <div className="font-mono-data text-indigo-900 font-bold leading-tight">{formatINR(summaryPivot.grand.net)}</div>
                        <div className="text-[9px] text-indigo-700 uppercase tracking-wider leading-tight">{summaryPivot.grand.count} inv</div>
                      </td>
                    </tr>
                    <tr className="bg-amber-50/60" data-testid="upload-summary-grand-total-cn">
                      <td className="px-3 py-2 border border-amber-300 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100">− Credit Notes</td>
                      {FY_MONTHS.map((m) => {
                        const c = summaryPivot.cnColTotal[m];
                        if (!c.count) return <td key={m} className="px-3 py-2 text-right text-slate-300 border border-amber-300">—</td>;
                        return (
                          <td key={m} className="px-3 py-2 text-right border border-amber-300 bg-amber-50">
                            <div className="font-mono-data text-amber-700 font-bold leading-tight">−{compactINR(c.total)}</div>
                            <div className="text-[9px] text-amber-500 uppercase tracking-wider leading-tight">{c.count} cn</div>
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right border border-amber-400 bg-amber-100">
                        <div className="font-mono-data text-amber-700 font-bold leading-tight">−{formatINR(summaryPivot.cnGrand.total)}</div>
                        <div className="text-[9px] text-amber-600 uppercase tracking-wider leading-tight">{summaryPivot.cnGrand.count} cn</div>
                      </td>
                    </tr>
                    <tr className="border-b-2 border-emerald-500 bg-emerald-100/70" data-testid="upload-summary-grand-total-net">
                      <td className="px-3 py-2 border border-emerald-400 text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-200/70">= Net</td>
                      {FY_MONTHS.map((m) => {
                        const c = summaryPivot.colTotal[m];
                        const cn = summaryPivot.cnColTotal[m];
                        if (!c.count && !cn.count) return <td key={m} className="px-3 py-2 text-right text-slate-300 border border-emerald-400">—</td>;
                        const netVal = (c.net || 0) - (cn.total || 0);
                        return (
                          <td key={m} className="px-3 py-2 text-right border border-emerald-400 bg-emerald-100/70" title={`Invoice ${formatINR(c.net)} − CN ${formatINR(cn.total)} = ${formatINR(netVal)}`}>
                            <div className="font-mono-data text-emerald-900 font-bold leading-tight">{compactINR(netVal)}</div>
                            <div className="text-[9px] text-emerald-700 uppercase tracking-wider leading-tight">net</div>
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right border border-emerald-500 bg-emerald-200">
                        <div className="font-mono-data text-emerald-900 font-bold leading-tight">{formatINR((summaryPivot.grand.net || 0) - (summaryPivot.cnGrand.total || 0))}</div>
                        <div className="text-[9px] text-emerald-700 uppercase tracking-wider leading-tight">net</div>
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 text-[10px] text-slate-400 border-t border-slate-200 bg-slate-50/60">
            Cell shows <strong>Net Amount</strong> (compact format). Hover any cell to see Base / GST / Net breakdown.
            {summaryPivot.hasAnyCN && (
              <span> Credit Notes are placed under their <strong>parent invoice's month</strong> and subtracted to produce the <strong className="text-emerald-700">Net</strong> row, so the net always reflects the recognised total for that month.</span>
            )}
          </div>
        </div>
      </div>

      {/* Uploaded Invoices list */}
      <div className="mb-4 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Uploaded Invoices</h2>
          <p className="text-xs text-slate-500 mt-1">
            {invoices.length} {debouncedQ ? `match${invoices.length === 1 ? "" : "es"}` : "invoices"} · Edit any record · Delete is allowed only when no entries (payments, retentions, vouchers) have been made.
          </p>
        </div>
        <div className="relative w-96">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input
            placeholder="Search invoice #, client, project or subvendor…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="rounded-none pl-9 pr-9 h-10 text-sm border-slate-300 focus-visible:ring-[#002FA7] focus-visible:border-[#002FA7]"
            data-testid="upload-search"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-3 top-3 text-slate-400 hover:text-slate-700 text-xs font-semibold"
              data-testid="upload-search-clear"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Invoice #</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Client</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Project</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Subvendor</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Invoice Date</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Net Amount</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr><td colSpan={8} className="text-center text-sm text-slate-400 py-12">No invoices uploaded yet.</td></tr>
            )}
            {pagedInvoices.map((inv) => (
              <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`upload-row-${inv.invoice_number}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{inv.invoice_number}</td>
                <td className="px-4 py-3 text-slate-700">{inv.party_client_name || "—"}</td>
                <td className="px-4 py-3 text-slate-700 text-xs">{inv.project || "—"}</td>
                <td className="px-4 py-3 text-slate-700 text-xs">{inv.subvendor_name || "—"}</td>
                <td className="px-4 py-3 text-slate-700 text-xs">{formatDate(inv.invoice_date)}</td>
                <td className="px-4 py-3 text-right font-mono-data text-slate-900">{formatINR(inv.net_amount)}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{inv.status || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => startEdit(inv)}
                    className="text-slate-600 hover:text-[#002FA7] inline-flex items-center gap-1 text-xs font-semibold mr-3"
                    data-testid={`edit-inv-${inv.invoice_number}`}
                  >
                    <Edit3 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => deleteInvoice(inv)}
                    disabled={inv.has_entries}
                    title={inv.has_entries ? "Cannot delete — entries exist against this invoice" : "Delete invoice"}
                    className={`inline-flex items-center gap-1 text-xs font-semibold ${
                      inv.has_entries
                        ? "text-slate-300 cursor-not-allowed"
                        : "text-red-500 hover:text-red-700"
                    }`}
                    data-testid={`del-inv-${inv.invoice_number}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={uplPage} setPage={setUplPage}
          pageSize={uplPageSize} setPageSize={setUplPageSize}
          totalPages={uplTotalPages} totalItems={uplTotalItems}
          testId="upload-pagination"
        />
      </div>
      </>
      )}

      <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="max-w-3xl rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display">Edit Invoice {editing?.invoice_number}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
              {EDIT_FIELDS.map(([k, label, type]) => (
                <div key={k}>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</Label>
                  <Input
                    type={type}
                    value={editing[k] ?? ""}
                    onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                    className="rounded-none mt-1"
                    data-testid={`edit-field-${k}`}
                  />
                </div>
              ))}
              <div className="col-span-2">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Description</Label>
                <Input
                  value={editing.invoice_description}
                  onChange={(e) => setEditing({ ...editing, invoice_description: e.target.value })}
                  className="rounded-none mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditOpen(false); setEditing(null); }} className="rounded-none">Cancel</Button>
            <Button onClick={submitEdit} data-testid="submit-edit-invoice" className="bg-[#002FA7] hover:bg-[#00227A] rounded-none">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
